import { Spacing, Text, Title } from '@vkontakte/vkui';
import plural from 'lib/plural';
import xray from 'lib/xray';
import React from 'react';
import { useSelector } from 'react-redux';
import { getFeatureQuotaCleanerDeletePromo } from 'reactApp/modules/features/features.selectors';
import { hasMailSubscription } from 'reactApp/modules/subscriptions/subscriptions.selectors';
import {
    getIsItemsDeleteError,
    getIsItemsDeleteProcess,
    isMeesagesRemoveNeeded,
} from 'reactApp/modules/userQuotaCleaner/userQuotaCleaner.selectors';
import { UserQuotaGroupId, UserQuotaGroupType } from 'reactApp/types/QuotaCleaner';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import Dialog from 'reactApp/ui/Dialog/Dialog';
import { useBuyCleanerTariff } from 'reactApp/ui/QuotaCleanerPage/hooks/useBuyCleanerTariff';
import { QuotaSpacePromo } from 'reactApp/ui/QuotaSpacePromo/QuotaSpacePromo';

import styles from './DeleteConfirmationDialog.css';

export interface DeleteConfirmationDialogProps {
    count: number;
    groupType: UserQuotaGroupType;
    groupId: UserQuotaGroupId;
    deleteSize: string;
    isTwoStepLettersDelete: boolean;
    onDelete: () => void;
    onLettersDelete: () => void;
    onClose: () => void;
    onGroupReload: () => void;
}

const pluralTitle = {
    [UserQuotaGroupType.Letter]: ['письмо', 'письма', 'писем'],
    [UserQuotaGroupType.Cloud]: ['файла', 'файлов', 'файлов'],
};

const pluralGroupTitle = {
    [UserQuotaGroupId.Mounted]: ['общей папки', 'общих папок', 'общих папок'],
};

const pluralGroupMessage = {
    [UserQuotaGroupId.Mounted]: ['общую папку', 'общие папки', 'общие папки'],
};

const getTitle = (groupId: UserQuotaGroupId, groupType: UserQuotaGroupType, count: number, isMessagesRemove: boolean): string => {
    if (groupId === UserQuotaGroupId.Mounted) {
        return `Отключение ${pluralGroupTitle[groupId][plural(count)]}`;
    }

    if (groupType === UserQuotaGroupType.Cloud) {
        return `Удаление ${count}\xA0${pluralTitle[groupType][plural(count)]}`;
    }

    if (isMessagesRemove) {
        return 'Удалить навсегда письма из корзины?';
    }

    return `Перенести ${count}\xA0${pluralTitle[groupType][plural(count)]} в корзину?`;
};

const getMessage = (groupId: UserQuotaGroupId, groupType: UserQuotaGroupType, count: number, isMessagesRemove: boolean) => {
    if (groupId === UserQuotaGroupId.Mounted) {
        return `Вы действительно хотите отключить ${pluralGroupMessage[groupId][plural(count)]} от своего Облака?`;
    }

    if (groupType === UserQuotaGroupType.Cloud) {
        return 'Вы действительно хотите перенести файлы в корзину?';
    }

    if (isMessagesRemove) {
        return 'Это удаление необратимо. Если письма не удалять из корзины, они будут храниться в ней 30 дней и занимать место.';
    }

    return 'Письма в корзине хранятся 30 дней, после автоматически удаляются.';
};

const getDeleteText = (groupType: UserQuotaGroupType, isMessagesRemove: boolean, deleteSize: string, deleteCount: number): string => {
    if (groupType === UserQuotaGroupType.Cloud) {
        return `Удалить ${deleteSize}`;
    }

    if (isMessagesRemove) {
        return `Удалить ${deleteCount} ${pluralTitle[groupType][plural(deleteCount)]}`;
    }

    return `В корзину ${deleteSize}`;
};

export const DeleteConfirmationDialog: React.FC<DeleteConfirmationDialogProps> = ({
    count,
    groupType,
    groupId,
    deleteSize,
    isTwoStepLettersDelete,
    onDelete,
    onLettersDelete,
    onClose,
    onGroupReload,
}) => {
    const isPaidUser = useSelector(hasMailSubscription);
    const isMessagesRemove = useSelector(isMeesagesRemoveNeeded);
    const isItemsDeleteProcess = useSelector(getIsItemsDeleteProcess);
    const isItemsDeleteError = useSelector(getIsItemsDeleteError);

    const { tariffPrice, tariffValue, onBuy } = useBuyCleanerTariff();
    const isQuotaPromo = useSelector(getFeatureQuotaCleanerDeletePromo);

    const title = getTitle(groupId, groupType, count, isMessagesRemove);
    const message = getMessage(groupId, groupType, count, isMessagesRemove);
    const deleteText = getDeleteText(groupType, isMessagesRemove, deleteSize, count);

    const prefix = isMessagesRemove ? 'msg-rmv' : 'trash';

    const onSubmit = React.useCallback(() => {
        if (isMessagesRemove) {
            onLettersDelete();
        } else {
            onDelete();
        }

        if ((isTwoStepLettersDelete && isMessagesRemove) || !isTwoStepLettersDelete || groupType === UserQuotaGroupType.Cloud) {
            onClose();
        }

        xray.send(`quota-cln-dlte-dlg-${prefix}-sbmt`, { i: groupId });
    }, [groupId, groupType, onDelete, onClose, isMessagesRemove, isTwoStepLettersDelete, onLettersDelete]);

    const onCancel = React.useCallback(() => {
        if (isMessagesRemove && isTwoStepLettersDelete && groupId !== UserQuotaGroupId.MailBin) {
            onGroupReload();
        }

        xray.send(`quota-cln-dlte-dlg-${prefix}-cls`, { i: groupId });
        onClose();
    }, [onClose, isMessagesRemove, isTwoStepLettersDelete]);

    React.useEffect(() => {
        xray.send(`quota-cln-dlte-dlg-${prefix}-shw`, { i: groupId });
    }, [isMessagesRemove]);

    React.useEffect(() => {
        if (isItemsDeleteError) {
            onClose();
        }
    }, [isItemsDeleteError]);

    return (
        <Dialog open id={`quota-delete-confirm:${groupType}:${prefix}`} size="small" closable onCancel={onCancel}>
            <div className={styles.wrapper}>
                <Title level="1" weight="2">
                    {title}
                </Title>
                <Spacing size={12} />
                <Text weight="3">{message}</Text>
                <Spacing size={24} />
                {isQuotaPromo && !isPaidUser && tariffPrice && (
                    <>
                        <QuotaSpacePromo tariffPrice={tariffPrice} tariffValue={tariffValue} onBuy={onBuy} />
                        <Spacing size={32} />
                    </>
                )}
                <div className={styles.buttons}>
                    <Button
                        theme="octavius"
                        sizeMode={ButtonSizeMode.small}
                        error
                        onClick={onSubmit}
                        qaId="delete-btn"
                        disabled={isItemsDeleteProcess}
                    >
                        <Text Component="h6" weight="2" style={{ color: 'var(--vkui--color_button_contrast)' }}>
                            {deleteText}
                        </Text>
                    </Button>
                    <div className={styles.cancel}>
                        <Button theme="octavius" sizeMode={ButtonSizeMode.small} secondary onClick={onCancel} qaId="cancel-btn">
                            <Text Component="h6" weight="2">
                                Отменить
                            </Text>
                        </Button>
                    </div>
                </div>
            </div>
        </Dialog>
    );
};
